import { FormsModule } from '@angular/forms';
import { Component, ElementRef, ViewChild, OnInit, OnDestroy, EventEmitter, Output, Input, NgZone } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { Camera } from '../../../models/camera';
import { SessionService } from '../../../services/session.service';
import { AuthenticationService } from '../../../services/authentication.service';
import { v4 as uuidv4 } from 'uuid';
import { NewSwingRequest } from '../../../models/newSwingRequest';
import { ApplicationService } from '../../../services/application.service';
import { ConfigService } from '../../../services/configuration.service';
import { CameraService } from '../../../services/camera.service';
import { Session } from '../../../models/session';
import { ensureMp4Blob, pickRecorderMimeType } from '../../../utils/video-mp4';


interface CaptureSettings {
  faceOnCameraId:string,
  downTheLineCameraId:string,
  delay:number,
  stopRecordingAfter:number
}


@Component({
  selector: 'app-captureswing',
  standalone: true,
  imports: [NgFor, FormsModule, NgIf],
  templateUrl: './captureswing.component.html',
  styleUrl: './captureswing.component.css'
})
export class CaptureswingComponent implements OnInit, OnDestroy  {
  @ViewChild('faceOnVideo') faceOnVideoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('downTheLineVideo') downTheLineVideoElement!: ElementRef<HTMLVideoElement>;

  @Input() session:Session|null = null;
  @Output() backToSessionClicked = new EventEmitter<void>();

  constructor(private sessionService:SessionService, private authenticationService:AuthenticationService, private applicationService:ApplicationService, private zone:NgZone, private configSerivce:ConfigService, private cameraService:CameraService) {}

  selectedFaceOnCamera = '';
  selectedDownTheLineCamera = '';
  availableCameras: Camera[] = [];
  swingRequest:NewSwingRequest = { id: '', dtlKey: '', faceOnKey: '', sessionId: this.session ? this.session.id : -1 };

  captureSettingsKey = `KRUMMYSWINGLAB-${this.configSerivce.environment}-CAPTURESETTINGS`;
  localCaptureSettings:CaptureSettings|null = null;
  
  // Recording properties
  isRecording = false;
  recordDelay = 8;
  autoStopAfter = 8;
  private recordingTimeout: any;
  private autoStopTimeout: any;
  currentFaceOnStream: MediaStream | null = null;
  currentDownTheLineStream: MediaStream | null = null;
  isBusy:boolean = false;

  private readonly deviceChangeHandler = () => {
    this.zone.run(() => {
      void this.getAvailableCameras();
    });
  };

  async ngOnInit() {
    if(!this.session) return;

    this.swingRequest.id = uuidv4();
    this.swingRequest.sessionId = this.session.id;

    navigator.mediaDevices?.addEventListener('devicechange', this.deviceChangeHandler);

    await this.getAvailableCameras();
    this.loadLocalCaptureSettings();
  }

  // Camera methods
  selectFaceOnCamera() {
    console.log('Face-on camera selected:', this.selectedFaceOnCamera);
    if (this.selectedFaceOnCamera) {
      this.setupCamera('face-on', this.selectedFaceOnCamera);
    }
  }

  selectDownTheLineCamera() {
    console.log('Down-the-line camera selected:', this.selectedDownTheLineCamera);
    if (this.selectedDownTheLineCamera) {
      this.setupCamera('down-the-line', this.selectedDownTheLineCamera);
    }
  }

  private async setupCamera(position: 'face-on' | 'down-the-line', cameraId: string) {
    try {
      // Stop existing stream
      const currentStream = position === 'face-on' ? this.currentFaceOnStream : this.currentDownTheLineStream;
      currentStream?.getTracks().forEach(track => track.stop());

      // Build constraints
      const constraints = cameraId === 'webcam'
        ? { video: true }
        : { video: { deviceId: { exact: cameraId } } };

      // Get new stream
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const track = stream.getVideoTracks()[0];
      if (track) {
        await this.cameraService.applySavedControls(track);
      }

      // Assign stream and video element
      const videoElementRef = position === 'face-on' ? this.faceOnVideoElement : this.downTheLineVideoElement;
      if (!videoElementRef) {
        console.error('Video element not initialized');
        return;
      }

      const videoEl = videoElementRef.nativeElement;
      videoEl.srcObject = stream;
      await videoEl.play();

      // Store stream reference
      if (position === 'face-on') {
        this.currentFaceOnStream = stream;
      } else {
        this.currentDownTheLineStream = stream;
      }

      console.log(`${position} camera (${cameraId}) setup complete`);
    } 
    catch (error) {
      console.error(`Error setting up ${position} camera:`, error);
    }
  }

  async getAvailableCameras() {
    try {
      const videoDevices = await this.cameraService.getAvailableCameras(this.inUseDeviceIds());
      this.availableCameras = [...videoDevices];
      this.availableCameras.push({ value: 'webcam', label: 'Webcam' });
    } catch (error) {
      console.error('Error getting available cameras:', error);
    }
  }

  private inUseDeviceIds(): Set<string> {
    const ids = new Set<string>();
    const collect = (stream: MediaStream | null) => {
      stream?.getVideoTracks().forEach(track => {
        const deviceId = track.getSettings().deviceId;
        if (deviceId) {
          ids.add(deviceId);
        }
      });
    };

    collect(this.currentFaceOnStream);
    collect(this.currentDownTheLineStream);
    return ids;
  }

  // Recording methods
  toggleRecording() {
    if (!this.isRecording) {
      this.startRecordingWithDelay();
    } else {
      this.stopRecording();
    }
  }

  private startRecordingWithDelay() {
    console.log(`Starting recording in ${this.recordDelay} seconds...`);

    let countdown = this.recordDelay;

    const countdownInterval = setInterval(() => {
      if (countdown <= 3 && countdown > 0) {
        this.applicationService.playBeep();
      }

      console.log(`Countdown: ${countdown}`);
      countdown--;

      if (countdown <= 0) {
        clearInterval(countdownInterval);

        // Start recording
        this.isRecording = true;
        console.log('Recording started!');

        this.autoStopTimeout = setTimeout(() => {
          this.stopRecording();
          console.log(`Recording auto-stopped after ${this.autoStopAfter} seconds`);
        }, this.autoStopAfter * 1000);

        this.startActualRecording();
      }
    }, 1000);
  }


  private stopRecording() {
    this.cleanupTimeouts();
    this.isRecording = false;
    console.log('Recording stopped...');
    
    // Add your actual recording stop logic here
    this.stopActualRecording();
  }

  recordedChunksFaceOn:Array<any> = [];
  recordedChunksDownLine:Array<any> = [];
  mediaRecorderFaceOn:MediaRecorder|null = null;
  mediaRecorderDownLine:MediaRecorder|null = null;

  uploadPromises: Promise<any>[] = [];
  expectedUploadStops:number = 0;
  uploadStoppedCount:number = 0;

  private startActualRecording() {
    this.recordedChunksFaceOn = [];
    this.recordedChunksDownLine = [];
    this.uploadPromises = [];
    this.expectedUploadStops = 0;
    this.uploadStoppedCount = 0;

    const mimeType = pickRecorderMimeType();
    const recorderOptions = mimeType ? { mimeType } : undefined;

    if(this.currentFaceOnStream) {
      this.mediaRecorderFaceOn = new MediaRecorder(this.currentFaceOnStream, recorderOptions);
    
      this.mediaRecorderFaceOn.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunksFaceOn.push(event.data);
        }
      };

      this.mediaRecorderFaceOn.onstop = () => {
        const blob = new Blob(this.recordedChunksFaceOn, {
          type: this.mediaRecorderFaceOn?.mimeType || mimeType || 'video/webm'
        });

        this.zone.run(async () => {
          if(!this.isBusy) {
            this.applicationService.showGlobalSpinner();
            this.isBusy = true;
          }
          try {
            const mp4Blob = await ensureMp4Blob(blob);
            const faceOnPromise = this.uploadVideo(mp4Blob, 'face-on.mp4');
            this.uploadPromises.push(faceOnPromise);
            this.checkUploadsComplete();
          } catch (error) {
            console.error('Failed to convert/upload face-on video', error);
            this.finishUploadFailure();
          }
        });
      };

      this.mediaRecorderFaceOn.start();
      this.expectedUploadStops++;
    }

    if(this.currentDownTheLineStream) {
      this.mediaRecorderDownLine = new MediaRecorder(this.currentDownTheLineStream, recorderOptions);

      this.mediaRecorderDownLine.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordedChunksDownLine.push(event.data);
        }
      };

      this.mediaRecorderDownLine.onstop = () => {
        const blob = new Blob(this.recordedChunksDownLine, {
          type: this.mediaRecorderDownLine?.mimeType || mimeType || 'video/webm'
        });
        this.zone.run(async () => {
          if(!this.isBusy) {
            this.applicationService.showGlobalSpinner();
            this.isBusy = true;
          }
          try {
            const mp4Blob = await ensureMp4Blob(blob);
            const dtlPromise = this.uploadVideo(mp4Blob, 'down-line.mp4');
            this.uploadPromises.push(dtlPromise);
            this.checkUploadsComplete();
          } catch (error) {
            console.error('Failed to convert/upload down-the-line video', error);
            this.finishUploadFailure();
          }
        });
      };

      this.mediaRecorderDownLine.start();
      this.expectedUploadStops++;
    }
  }

  async uploadVideo(blob: Blob, filename: string):Promise<string> {
    if(!this.session)
      throw Error("No session to upload video to.");

    let key = `${this.session?.username}/${this.session.id}/${this.swingRequest.id}/${filename}`;
    let contentType = "video/mp4";

    let uploadUrl = await this.sessionService.getUploadUrl(key, contentType);

    await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "Content-Type": contentType,
      },
      body: blob
    });
   
    if(filename === "face-on.mp4") {
      this.swingRequest.faceOnKey = key;
    }
    else {
      this.swingRequest.dtlKey = key;
    }

    return key;
  }

  private finishUploadFailure() {
    if (this.isBusy) {
      this.applicationService.hideGlobalSpinner();
      this.isBusy = false;
    }
  }

  checkUploadsComplete = () => {
    this.uploadStoppedCount++;
    if (this.uploadStoppedCount === this.expectedUploadStops) {
      Promise.all(this.uploadPromises).then(() => {
        this.saveSwing();
      });
    }
  };

  async saveSwing() {
    await this.sessionService.insertSwing(this.swingRequest);
    
    this.saveLocalCaptureSettings();

    if(this.isBusy) {
      this.applicationService.hideGlobalSpinner();
      this.isBusy = false;
      this.backButtonClicked();
    }
  }


  private stopActualRecording() {
    this.mediaRecorderDownLine?.stop();
    this.mediaRecorderFaceOn?.stop();
  }

  private cleanupTimeouts() {
    if (this.recordingTimeout) {
      clearTimeout(this.recordingTimeout);
      this.recordingTimeout = null;
    }
    
    if (this.autoStopTimeout) {
      clearTimeout(this.autoStopTimeout);
      this.autoStopTimeout = null;
    }
  }

  updateRecordDelay() {
    console.log(`Record delay set to: ${this.recordDelay} seconds`);
  }

  updateAutoStop() {
    console.log(`Auto-stop set to: ${this.autoStopAfter} seconds`);
  }

  private async stopStream(stream: MediaStream | null) {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
       // 🔁 Give the browser a tick to fully disconnect
      await new Promise(resolve => setTimeout(resolve, 5000));
    }
  }

  backButtonClicked() {
    this.backToSessionClicked.emit();
  }

  loadLocalCaptureSettings() {
    let rawSettings = localStorage.getItem(this.captureSettingsKey);
    this.localCaptureSettings = rawSettings? JSON.parse(rawSettings) as CaptureSettings : null;
    if(this.localCaptureSettings) {
      this.recordDelay = this.localCaptureSettings.delay;
      this.autoStopAfter = this.localCaptureSettings.stopRecordingAfter;
      this.selectedDownTheLineCamera = this.localCaptureSettings.downTheLineCameraId;
      this.selectedFaceOnCamera = this.localCaptureSettings.faceOnCameraId;

      if(this.selectedDownTheLineCamera.length > 0)
        this.selectDownTheLineCamera();
      
      if(this.selectedFaceOnCamera.length > 0)
        this.selectFaceOnCamera();
    }
  }

  saveLocalCaptureSettings() {
    let settings:CaptureSettings = {
      delay: this.recordDelay,
      downTheLineCameraId: this.selectedDownTheLineCamera,
      faceOnCameraId: this.selectedFaceOnCamera,
      stopRecordingAfter: this.autoStopAfter
    };

    localStorage.setItem(this.captureSettingsKey, JSON.stringify(settings));
  }

  async ngOnDestroy() {
    navigator.mediaDevices?.removeEventListener('devicechange', this.deviceChangeHandler);
    this.cleanupTimeouts();
    await this.stopStream(this.currentDownTheLineStream);
    await this.stopStream(this.currentFaceOnStream);
    this.faceOnVideoElement.nativeElement.srcObject = null;
    this.downTheLineVideoElement.nativeElement.srcObject = null;
  }
}
